import type PlyrType from 'plyr';
import type { Options as PlyrOptions } from 'plyr';
import type Hls from 'hls.js';
import type { ErrorData } from 'hls.js';
import type * as Alpine from 'alpinejs';

const TIMEOUTS = {
    BANNER_GOTO: 10_000,
    PRIVATE_TIMEOUT: 60_000,
    VOLUME_CHANGE_DEBOUNCE: 250,
};

const DEFAULT_PLYR_OPTIONS: PlyrOptions = {
    clickToPlay: false,
    autoplay: true,
    muted: true,
    controls: ['mute', 'volume', 'fullscreen'],
};

class StreamPlayer {
    private player: PlyrType | null = null;
    private hls: Hls | null = null;
    private video: HTMLVideoElement | null = null;
    private container: HTMLElement | null = null;
    private retries = 0;
    private bannerTimeout: ReturnType<typeof setTimeout> | null = null;
    private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    private offlineSources: any[] = [];

    constructor() {
        this.init();
    }

    private dispatch(eventName: string, detail: boolean = true) {
        window.dispatchEvent(new CustomEvent(eventName, { detail }));
    }

    public async init() {
        this.scheduleBannerHide(1500);

        this.container = document.querySelector<HTMLElement>('.video-container');
        if (!this.container) return;

        this.video = this.container.querySelector<HTMLVideoElement>('video');
        if (!this.video) return;

        this.resetMedia();

        const alpineData = Alpine?.closestDataStack(this.video)?.[0] as any;

        this.offlineSources = alpineData.offlineSources as any[];

        if (alpineData.isOnline && !alpineData.isPrivate) {
            await this.loadVideo();
        } else {
            await this.loadOfflineVideo();
        }

        this.container.addEventListener('click', this.handleContainerClick);
    }

    private resetMedia() {
        if (this.hls) {
            try {
                this.hls.stopLoad();
                this.hls.detachMedia();
                this.hls.destroy();
            } catch (e) {}

            this.hls = null;
        }

        if (this.player) {
            try { this.player.destroy(); } catch (e) {}
            this.player = null;
        }

        if (this.video) {
            this.video.pause();
            this.video.loop = false;
            this.video.removeAttribute('src');

            while (this.video.firstChild) {
                this.video.removeChild(this.video.firstChild);
            }
        }
    }

    private async loadOfflineVideo() {
        if (!this.video) return;

        this.resetMedia();

        this.dispatch('stream-goto', true);

        console.log('HLS: Switching to offline video ->', this.offlineSources);

        this.video.removeAttribute('src');
        this.video.muted = true;
        this.video.loop = true;
        this.video.defaultMuted = true;

        this.offlineSources.forEach((v) => {
            const source = document.createElement('source');
            source.src = v.src;
            source.type = v.type;

            this.video.append(source);
        });

        //this.video.load();

        await this.startPlayer({
            ...DEFAULT_PLYR_OPTIONS,
            controls: [],
            clickToPlay: false,
            loop: { active: true },
            muted: true,
            autoplay: true,
        });

        this.dispatch('stream-playing');
        this.dispatch('stream-buffering', false);
    }

    private async loadVideo() {
        if (!this.video) return;

        this.resetMedia();

        const source = this.video.getAttribute('data-stream');
        if (!source) return;

        if (this.video.canPlayType('application/vnd.apple.mpegurl')) {
            this.video.setAttribute('src', source);
            await this.startPlayer();
            return;
        }

        const { default: HlsClass } = await import('hls.js/dist/hls.light.mjs');

        if (!HlsClass.isSupported()) {
            await this.startPlayer();
            return;
        }

        this.hls = new HlsClass({
            enableWorker: true,
            lowLatencyMode: true,
            backBufferLength: 30,
            maxBufferLength: 10,
        });

        this.hls.on(HlsClass.Events.ERROR, (_, data) => this.handleHlsFatalError(HlsClass, data));
        this.hls.on(HlsClass.Events.FRAG_LOADED, () => { this.retries = 0; });

        this.hls.on(HlsClass.Events.MANIFEST_PARSED, async (_, data) => {
            const options = { ...DEFAULT_PLYR_OPTIONS };
            options.quality = this.buildQualityOptions(data.levels);
            await this.startPlayer(options);
        });

        this.hls.attachMedia(this.video);

        this.hls.on(HlsClass.Events.MEDIA_ATTACHED, () => {
            this.hls!.loadSource(source);
        });
    }

    private async startPlayer(options: PlyrOptions = DEFAULT_PLYR_OPTIONS) {
        if (!this.video) return;

        const { default: PlyrClass } = await import('plyr');

        //console.log(this.video);

        this.player = new PlyrClass(this.video, options);

        this.player.on('play', (event) => {
            (event.target as HTMLElement).setAttribute('data-muted', String(this.player!.muted));
        });

        this.player.on('volumechange', (event) => {
            setTimeout(() => {
                if (this.player) {
                    (event.target as HTMLElement).setAttribute('data-muted', String(this.player.muted));
                }
            }, TIMEOUTS.VOLUME_CHANGE_DEBOUNCE);
        });

        this.player.on('waiting', () => this.dispatch('stream-buffering', true));
        this.player.on('canplay', () => this.dispatch('stream-buffering', false));
        this.player.on('playing', () => {
            this.dispatch('stream-buffering', false);
            this.dispatch('stream-playing');
        });

        if (this.video.readyState >= 3) this.dispatch('stream-buffering', false);
        if (!this.video.paused) this.dispatch('stream-playing');

        const playPromise = this.player.play();
        if (playPromise !== undefined) {
            playPromise.then(() => {
                this.dispatch('stream-playing');
                this.dispatch('stream-buffering', false);
            }).catch((e) => {
                this.dispatch('stream-playing');
                this.dispatch('stream-buffering', false);

                if (e.name === 'AbortError') {
                    setTimeout(() => {
                        if (this.player && this.video?.paused) {
                            this.player.play().catch(() => {});
                        }
                    }, 100);
                }
            });
        }
    }

    private handleHlsFatalError(HlsClass: typeof Hls, data: ErrorData) {
        if (!this.hls) return;

        console.log(data.type, data.response);

        switch (data.type) {
            case HlsClass.ErrorTypes.NETWORK_ERROR:
                if (data.response && [401, 404, 405].includes(data.response.code)) {
                    console.log('HLS: Stream offline (404).');
                    this.dispatch('stream-offline', true);
                    this.loadOfflineVideo();
                } else if (data.response && data.response.code == 403) {
                    console.log('HLS: 403 Forbidden. Switching to private mode.');
                    this.dispatch('stream-private', true);
                    this.loadOfflineVideo();
                    this.schedulePrivateReconnect();
                } else {
                    this.retries++;
                    if (this.retries > 3) {
                        this.dispatch('stream-offline', true);
                        this.loadOfflineVideo();
                    } else {
                        this.hls.startLoad();
                    }
                }
                break;
            case HlsClass.ErrorTypes.MEDIA_ERROR:
                this.hls.recoverMediaError();
                break;
            default:
                this.dispatch('stream-offline', true);
                this.loadOfflineVideo();
        }
    }

    private buildQualityOptions(levels: any[]): PlyrOptions['quality'] {
        const options = levels.map((l) => l.height);
        return {
            default: options[0], options, forced: true,
            onChange: (newQuality: number) => {
                if (!this.hls) return;
                this.hls.levels.forEach((level, index) => {
                    if (level.height === newQuality) this.hls!.currentLevel = index;
                });
            },
        };
    }

    private handleContainerClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement;

        if (target.closest('.close button') || target.closest('.close > flux\\:button')) {
            this.dispatch('stream-goto', false);
            this.scheduleBannerHide();
        }

        if (target.closest('.plyr + .muted') && this.player) {
            this.player.volume = 1;
        }
    };

    private schedulePrivateReconnect() {
        if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);

        this.reconnectTimeout = setTimeout(async () => {
            if (!this.video) return;
            console.log('HLS: Attempting to recover stream...');
            this.dispatch('stream-private', false);
            await this.loadVideo();
        }, TIMEOUTS.PRIVATE_TIMEOUT);
    }

    private scheduleBannerHide(timeout: number = TIMEOUTS.BANNER_GOTO) {
        if (this.bannerTimeout) clearTimeout(this.bannerTimeout);

        this.bannerTimeout = setTimeout(() => {
            this.dispatch('stream-goto', true);
        }, timeout);
    }

    public destroy() {
        if (this.bannerTimeout) clearTimeout(this.bannerTimeout);
        if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
        if (this.container) this.container.removeEventListener('click', this.handleContainerClick);

        this.resetMedia();
    }
}

// === Управление Жизненным Циклом ===
let playerInstance: StreamPlayer | null = null;

function mountPlayer(timeout: number = 3000) {
    if (playerInstance) playerInstance.destroy();

    const videoContainer = document.querySelector('.video-container');
    if (!videoContainer) return;

    const startHls = () => {
        if (!playerInstance) {
            playerInstance = new StreamPlayer();
        }

        ['scroll', 'resize', 'mousemove', 'touchstart', 'click', 'keydown'].forEach(event => {
            window.removeEventListener(event, startHls);
        });
    };

    ['scroll', 'resize', 'mousemove', 'touchstart', 'click', 'keydown'].forEach(event => {
        window.addEventListener(event, startHls, { once: true, passive: true });
    });

    setTimeout(startHls, timeout);
}

document.addEventListener('livewire:init', () => {
    localStorage.removeItem('plyr');
});

document.addEventListener('livewire:navigated', () => {
    if (playerInstance) {
        playerInstance.destroy();
        playerInstance = null;
    }

    mountPlayer();
});

document.addEventListener('livewire:navigating', () => {
    if (playerInstance) {
        playerInstance.destroy();
        playerInstance = null;
    }
});

mountPlayer();
